import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp, onSnapshot, query, orderBy, getDocs, doc, updateDoc, deleteDoc, arrayUnion, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { User, UserManagementUser } from '../types';
import { getApiUrl } from '../config';
import { getEffectiveRole, canSeeSitemap } from '../utils/roleUtils';
import { AddUserModal } from './AddUserModal';
import { getCompletionColor, calculateCaseFormCompletion, calculateOverallProcessCompletion, calculateMeetingCompletion, countCaseMeetings } from '../utils/intakeCompletion';

interface Program {
  id: string;
  name: string;
  description: string;
  logoUrl?: string;
  managers: string[]; // Array of user IDs
  managerNames: string[]; // Array of user names for display
  organizationId?: string; // Organization ID this program belongs to
  organizationName?: string; // Organization name for display
  startDate?: string;
  endDate?: string;
  status: 'active' | 'inactive' | 'completed' | 'planned';
  createdBy: string;
  createdByEmail: string;
  createdAt: any;
}

interface Organization {
  id: string;
  name: string;
  description: string;
  logoUrl?: string;
  administrators: string[];
  administratorNames: string[];
  createdBy: string;
  createdByEmail: string;
  createdAt: any;
  requiredMeetings?: number; // Number of meetings required to complete the process
}

interface ProgramsProps {
  user: User;
}

type WizardStep = 'basic' | 'managers' | 'details';

export default function Programs({ user }: ProgramsProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { organizationId } = getEffectiveRole(user);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [currentStep, setCurrentStep] = useState<WizardStep>('basic');
  const [isUploading, setIsUploading] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsViewMode, setDetailsViewMode] = useState<'view' | 'edit'>('view');
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    logoUrl: '',
    managers: [] as string[],
    managerNames: [] as string[],
    organizationId: '',
    organizationName: '',
    startDate: '',
    endDate: '',
    status: 'planned' as Program['status']
  });

  // User search for wizard
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<UserManagementUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // User search for edit modal
  const [detailsUserSearchTerm, setDetailsUserSearchTerm] = useState('');
  const [detailsSearchResults, setDetailsSearchResults] = useState<UserManagementUser[]>([]);
  const [isDetailsSearching, setIsDetailsSearching] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [allUsers, setAllUsers] = useState<UserManagementUser[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [programCompletions, setProgramCompletions] = useState<{ [programId: string]: number }>({});
  const [programMeetingCompletions, setProgramMeetingCompletions] = useState<{ [programId: string]: { completed: number; total: number } }>({});

  // Edit modal data
  const [editData, setEditData] = useState({
    name: '',
    description: '',
    logoUrl: '',
    managers: [] as string[],
    managerNames: [] as string[],
    startDate: '',
    endDate: '',
    status: 'active' as Program['status']
  });

  // Load organizations
  useEffect(() => {
    const q = query(collection(db, 'organizations'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const orgsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Organization[];
      setOrganizations(orgsData);
    });

    return () => unsubscribe();
  }, []);

  // Load all users for member display
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const usersRef = collection(db, 'users');
        const snapshot = await getDocs(usersRef);
        const users: UserManagementUser[] = [];
        
        snapshot.forEach((doc) => {
          const userData = doc.data();
          if (!userData.deleted) {
            users.push({
              id: doc.id,
              name: userData.name || '',
              email: userData.email || '',
              role: userData.role,
              organizationId: userData.organizationId,
              organizationRole: userData.organizationRole,
              groupId: userData.groupId,
              programId: userData.programId,
              createdAt: userData.createdAt?.toDate?.()?.toISOString() || '',
              lastLoginAt: userData.lastLoginAt?.toDate?.()?.toISOString() || '',
              loginCount: userData.loginCount || 0,
              blocked: userData.blocked || false
            });
          }
        });
        setAllUsers(users);
      } catch (error) {
        console.error('Error loading users:', error);
      }
    };
    loadUsers();
  }, []);

  // Load groups
  useEffect(() => {
    const q = query(collection(db, 'groups'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const groupsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setGroups(groupsData);
    });

    return () => unsubscribe();
  }, []);

  // Load programs
  useEffect(() => {
    const q = query(collection(db, 'programs'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const programsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Program[];
      setPrograms(programsData);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Calculate program completion statistics based on groups
  useEffect(() => {
    const calculateProgramCompletions = async () => {
      if (programs.length === 0 || groups.length === 0 || allUsers.length === 0) {
        setProgramCompletions({});
        return;
      }

      try {
        // Load all patients
        const { getAllPatients } = await import('../firebase');
        const allPatients = await getAllPatients();
        const activePatients = allPatients.filter(p => p.status === 'active');

        if (activePatients.length === 0) {
          setProgramCompletions({});
          return;
        }

        // Import form management functions
        const { getAllForms, getCaseSubmissions } = await import('../firebase/formManagement');
        const allForms = await getAllForms();

        const completions: { [programId: string]: number } = {};
        const meetingCompletions: { [programId: string]: { completed: number; total: number } } = {};

        // Calculate completion for each program
        for (const program of programs) {
          // Get all groups belonging to this program
          const programGroups = groups.filter(g => g.programId === program.id);
          
          if (programGroups.length === 0) {
            completions[program.id] = 0;
            meetingCompletions[program.id] = { completed: 0, total: 0 };
            continue;
          }

          // Get all members of all groups in this program
          const programGroupIds = programGroups.map(g => g.id);
          const programMembers = allUsers.filter(u => 
            u.groupId && programGroupIds.includes(u.groupId)
          );
          
          if (programMembers.length === 0) {
            completions[program.id] = 0;
            meetingCompletions[program.id] = { completed: 0, total: 0 };
            continue;
          }

          // Get required meetings from parent organization
          const parentOrg = program.organizationId ? organizations.find(o => o.id === program.organizationId) : null;
          const requiredMeetings = parentOrg?.requiredMeetings || 0;

          // Collect all intake completions and count meetings
          const allIntakeCompletions: number[] = [];
          let totalCompletedMeetings = 0;
          let totalRequiredMeetings = 0;

          // Get all active cases belonging to this program (regardless of assignment)
          const programCases = activePatients.filter(p => {
            // Must belong to this program (directly or through group)
            if (p.programId === program.id) {
              return true;
            }
            // Check if patient belongs to a group in this program
            if (p.groupId && programGroupIds.includes(p.groupId)) {
              return true;
            }
            return false;
          });

          // Calculate intake and count meetings for all cases in this program
          for (const patient of programCases) {
            try {
              // Calculate intake completion (only for cases assigned to program members)
              const isAssignedToProgramMember = programMembers.some(member => 
                Array.isArray(patient.assignedAdmins) && patient.assignedAdmins.includes(member.id)
              );
              
              if (isAssignedToProgramMember) {
                const intakeCompletion = await calculateCaseFormCompletion(patient.caseId);
                // Include all cases, even if completion is 0%
                allIntakeCompletions.push(intakeCompletion);
              }
              
              // Count meetings: completed and total from events DB (for all cases in program)
              const meetingCounts = await countCaseMeetings(patient.caseId);
              totalCompletedMeetings += meetingCounts.completed;
              totalRequiredMeetings += meetingCounts.total;
            } catch (error) {
              console.error(`Error calculating completion for case ${patient.caseId}:`, error);
            }
          }

          // Calculate average intake completion
          if (allIntakeCompletions.length === 0) {
            completions[program.id] = 0;
          } else {
            const totalIntakeCompletion = allIntakeCompletions.reduce((sum, comp) => sum + comp, 0);
            const averageIntakeCompletion = Math.round(totalIntakeCompletion / allIntakeCompletions.length);
            completions[program.id] = averageIntakeCompletion;
          }

          // Set meeting count as completed/all
          meetingCompletions[program.id] = totalRequiredMeetings > 0 
            ? { completed: totalCompletedMeetings, total: totalRequiredMeetings }
            : { completed: 0, total: 0 };
        }

        setProgramCompletions(completions);
        setProgramMeetingCompletions(meetingCompletions);
      } catch (error) {
        console.error('Error calculating program completions:', error);
        setProgramCompletions({});
        setProgramMeetingCompletions({});
      }
    };

    calculateProgramCompletions();
  }, [programs, groups, allUsers, organizations]);

  // Search users
  const searchUsers = async (searchTerm: string) => {
    if (searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      const users = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter((user: any) => !user.deleted) as UserManagementUser[];

      const filtered = users.filter(user => 
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );

      setSearchResults(filtered);
    } catch (error) {
      console.error('Error searching users:', error);
    } finally {
      setIsSearching(false);
    }
  };

  // Handle user search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchUsers(userSearchTerm);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [userSearchTerm]);

  // Search users for edit modal
  const searchDetailsUsers = async (searchTerm: string) => {
    if (searchTerm.length < 2) {
      setDetailsSearchResults([]);
      return;
    }

    setIsDetailsSearching(true);
    try {
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      const users: UserManagementUser[] = [];
      
      snapshot.forEach((doc) => {
        const userData = doc.data();
        if (!userData.deleted) {
          const user: UserManagementUser = {
            id: doc.id,
            name: userData.name || '',
            email: userData.email || '',
            role: userData.role || '',
            createdAt: userData.createdAt?.toDate?.()?.toISOString() || '',
            lastLoginAt: userData.lastLoginAt?.toDate?.()?.toISOString() || '',
            loginCount: userData.loginCount || 0,
            blocked: userData.blocked || false
          };
          
          // Filter by search term and exclude already assigned users
          if ((user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              user.email.toLowerCase().includes(searchTerm.toLowerCase())) &&
              !editData.managers.includes(user.id)) {
            users.push(user);
          }
        }
      });
      
      setDetailsSearchResults(users);
    } catch (error) {
      console.error('Error searching users:', error);
    } finally {
      setIsDetailsSearching(false);
    }
  };

  // Handle user search for edit modal
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchDetailsUsers(detailsUserSearchTerm);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [detailsUserSearchTerm, editData.managers]);

  // Add manager to program
  const handleAddManager = (user: UserManagementUser) => {
    if (!formData.managers.includes(user.id)) {
      setFormData(prev => ({
        ...prev,
        managers: [...prev.managers, user.id],
        managerNames: [...prev.managerNames, user.name]
      }));
    }
    setUserSearchTerm('');
    setSearchResults([]);
  };

  const handleRemoveManager = (userId: string) => {
    const index = formData.managers.indexOf(userId);
    if (index > -1) {
      setFormData(prev => ({
        ...prev,
        managers: prev.managers.filter(id => id !== userId),
        managerNames: prev.managerNames.filter((_, i) => i !== index)
      }));
    }
  };

  // Add manager to edit modal
  const handleAddDetailsManager = (user: UserManagementUser) => {
    if (!editData.managers.includes(user.id)) {
      setEditData(prev => ({
        ...prev,
        managers: [...prev.managers, user.id],
        managerNames: [...prev.managerNames, user.name]
      }));
    }
    setDetailsUserSearchTerm('');
    setDetailsSearchResults([]);
  };

  // Remove manager from edit modal
  const handleRemoveDetailsManager = async (userId: string) => {
    if (!selectedProgram) return;
    
    try {
      // Remove from program's managers array
      const programRef = doc(db, 'programs', selectedProgram.id);
      const programDoc = await getDoc(programRef);
      if (programDoc.exists()) {
        const programData = programDoc.data();
        const currentManagers = programData.managers || [];
        const currentManagerNames = programData.managerNames || [];
        const index = currentManagers.indexOf(userId);
        
        if (index > -1) {
          await updateDoc(programRef, {
            managers: currentManagers.filter((id: string) => id !== userId),
            managerNames: currentManagerNames.filter((_: string, i: number) => i !== index)
          });
        }
      }
      
      // Update local state
      const index = editData.managers.indexOf(userId);
      if (index > -1) {
        setEditData(prev => ({
          ...prev,
          managers: prev.managers.filter(id => id !== userId),
          managerNames: prev.managerNames.filter((_, i) => i !== index)
        }));
      }
    } catch (error) {
      console.error('Error removing manager:', error);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!selectedProgram) return;
    
    try {
      // Remove user's programId (remove from program)
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        programId: null
      });
      
      // If user was also a manager, remove from managers array
      if (editData.managers.includes(userId)) {
        await handleRemoveDetailsManager(userId);
      }
      
      // Reload users to refresh the list
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      const users: UserManagementUser[] = [];
      
      snapshot.forEach((doc) => {
        const userData = doc.data();
        if (!userData.deleted) {
          users.push({
            id: doc.id,
            name: userData.name || '',
            email: userData.email || '',
            role: userData.role,
            organizationId: userData.organizationId,
            organizationRole: userData.organizationRole,
            groupId: userData.groupId,
            programId: userData.programId,
            createdAt: userData.createdAt?.toDate?.()?.toISOString() || '',
            lastLoginAt: userData.lastLoginAt?.toDate?.()?.toISOString() || '',
            loginCount: userData.loginCount || 0,
            blocked: userData.blocked || false
          });
        }
      });
      setAllUsers(users);
    } catch (error) {
      console.error('Error removing member:', error);
    }
  };


  // Logo upload handler
  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      // Validate and resize image to max 1000x1000 JPG
      const { validateAndResizeIcon } = await import('../utils');
      const resizedFile = await validateAndResizeIcon(file);

      const formDataUpload = new FormData();
      formDataUpload.append('file', resizedFile);

      const response = await fetch(getApiUrl('/upload'), {
        method: 'POST',
        body: formDataUpload,
      });

      if (response.ok) {
        const result = await response.json();
        // Add cache-busting parameter to force image reload
        const imageUrl = result.file_url + (result.file_url.includes('?') ? '&' : '?') + 't=' + Date.now();
        setFormData(prev => ({ ...prev, logoUrl: imageUrl }));
      } else {
        throw new Error('Upload failed');
      }
    } catch (error: any) {
      console.error('Error uploading logo:', error);
      alert(error.message || 'Failed to upload icon. Please ensure the image is JPG format and try again.');
    } finally {
      setIsUploading(false);
      // Reset file input to allow selecting the same file again
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  // Create program
  const handleCreateProgram = async () => {
    if (!formData.name.trim() || !formData.organizationId) {
      return;
    }

    setIsCreating(true);
    try {
      await addDoc(collection(db, 'programs'), {
        name: formData.name.trim(),
        description: formData.description.trim(),
        logoUrl: formData.logoUrl,
        managers: formData.managers,
        managerNames: formData.managerNames,
        organizationId: formData.organizationId,
        organizationName: formData.organizationName,
        createdBy: user.name,
        createdByEmail: user.email,
        createdAt: serverTimestamp()
      });

      // Reset form
      setFormData({
        name: '',
        description: '',
        logoUrl: '',
        managers: [],
        managerNames: [],
        organizationId: '',
        organizationName: '',
        startDate: '',
        endDate: '',
        status: 'planned'
      });
      setShowWizard(false);
      setCurrentStep('basic');
      // Program created successfully
    } catch (error) {
      console.error('Error creating program:', error);
      console.error('Create error');
    } finally {
      setIsCreating(false);
    }
  };

  // Logo upload handler for edit modal
  const handleDetailsLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      // Validate and resize image to max 1000x1000 JPG
      const { validateAndResizeIcon } = await import('../utils');
      const resizedFile = await validateAndResizeIcon(file);

      const formDataUpload = new FormData();
      formDataUpload.append('file', resizedFile);

      const response = await fetch(getApiUrl('/upload'), {
        method: 'POST',
        body: formDataUpload,
      });

      if (response.ok) {
        const result = await response.json();
        // Add cache-busting parameter to force image reload
        const imageUrl = result.file_url + (result.file_url.includes('?') ? '&' : '?') + 't=' + Date.now();
        setEditData(prev => ({ ...prev, logoUrl: imageUrl }));
      } else {
        throw new Error('Upload failed');
      }
    } catch (error: any) {
      console.error('Error uploading logo:', error);
      alert(error.message || 'Failed to upload icon. Please ensure the image is JPG format and try again.');
    } finally {
      setIsUploading(false);
      // Reset file input to allow selecting the same file again
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  // Update program
  const handleUpdateProgram = async () => {
    if (!selectedProgram || !editData.name.trim()) return;

    setIsUpdating(true);
    try {
      const programRef = doc(db, 'programs', selectedProgram.id);
      await updateDoc(programRef, {
        name: editData.name,
        description: editData.description,
        logoUrl: editData.logoUrl,
        managers: editData.managers,
        managerNames: editData.managerNames
      });

      setShowDetailsModal(false);
      // Program updated successfully
    } catch (error) {
      console.error('Error updating program:', error);
      console.error('Update error');
    } finally {
      setIsUpdating(false);
    }
  };

  // Delete program
  const handleDeleteProgram = async (programId: string) => {
    // Delete program

    setIsDeleting(true);
    try {
      await deleteDoc(doc(db, 'programs', programId));
      // Program deleted successfully - close modal and reset selected program
      setShowDetailsModal(false);
      setSelectedProgram(null);
    } catch (error) {
      console.error('Error deleting program:', error);
      console.error('Delete error');
    } finally {
      setIsDeleting(false);
    }
  };

  // Open details modal
  const handleOpenDetails = (program: Program) => {
    setSelectedProgram(program);
    setEditData({
      name: program.name,
      description: program.description || '',
      logoUrl: program.logoUrl || '',
      managers: program.managers || [],
      managerNames: program.managerNames || [],
      startDate: program.startDate || '',
      endDate: program.endDate || '',
      status: program.status
    });
    setDetailsViewMode('view');
    setDetailsUserSearchTerm('');
    setDetailsSearchResults([]);
    setShowDetailsModal(true);
  };



  if (isLoading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div style={{ fontSize: '18px', color: '#666' }}>
          {t('programs.loading')}...
        </div>
      </div>
    );
  }

  return (
    <div className="programs-container" style={{ padding: '20px' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '30px'
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '28px', fontWeight: 600, color: '#333' }}>
            {t('programs.title')}
          </h2>
          <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '16px' }}>
            {t('programs.subtitle')}
          </p>
        </div>
        <button
          onClick={() => setShowWizard(true)}
          style={{
            background: '#007acc',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          ➕ {t('programs.createNew')}
        </button>
      </div>

      {/* Programs Grid */}
      {programs.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: '#666'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '20px' }}>
            {t('programs.noPrograms')}
          </h3>
          <p style={{ margin: 0, fontSize: '14px', color: '#999' }}>
            {t('programs.createFirstProgram')}
          </p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
          gap: '20px'
        }}>
          {programs.map((program) => {
          const isRTL = i18n.language === 'he';
          return (
            <div
              key={program.id}
              onClick={() => handleOpenDetails(program)}
              style={{
                backgroundColor: '#ffffff',
                border: '1px solid #e0e0e0',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: isRTL ? 'row-reverse' : 'row',
                height: '100%'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.backgroundColor = '#f8f9fa';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.backgroundColor = '#ffffff';
              }}
            >
              {/* Content Section */}
              <div style={{ padding: '24px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <h3 style={{ 
                  margin: '0 0 10px 0', 
                  color: '#1e293b',
                  fontSize: '20px',
                  fontWeight: '600',
                  lineHeight: '1.3'
                }}>
                  {program.name}
                </h3>
                {program.description && (
                  <p style={{
                    margin: '0 0 14px 0',
                    color: '#64748b',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    flex: 1
                  }}>
                    {program.description}
                  </p>
                )}

                {/* Completion Statistics */}
                <div style={{
                  marginTop: 'auto',
                  paddingTop: '12px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}>
                  {/* Intake Completion */}
                  {programCompletions[program.id] !== undefined && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span style={{ fontSize: '11px', color: '#666', minWidth: '50px' }}>
                        {i18n.language === 'he' ? 'קליטה:' : 'Intake:'}
                      </span>
                      <div style={{
                        width: '60px',
                        height: '6px',
                        backgroundColor: '#e0e0e0',
                        borderRadius: '3px',
                        overflow: 'hidden',
                        position: 'relative'
                      }}>
                        <div
                          style={{
                            width: `${programCompletions[program.id]}%`,
                            height: '100%',
                            backgroundColor: getCompletionColor(programCompletions[program.id]),
                            transition: 'width 0.3s ease'
                          }}
                        />
                      </div>
                      <span style={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: getCompletionColor(programCompletions[program.id]),
                        minWidth: '35px'
                      }}>
                        {programCompletions[program.id]}%
                      </span>
                    </div>
                  )}
                </div>
              
              </div>
              
              {/* Image Section - Right side */}
              <div style={{
                width: '200px',
                minWidth: '200px',
                height: '100%',
                backgroundColor: '#f3f4f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                flexShrink: 0
              }}>
                {program.logoUrl ? (
                  <img
                    src={program.logoUrl}
                    alt={`${program.name} logo`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <div style={{
                    fontSize: '64px',
                    color: '#6b7280'
                  }}>
                    📋
                  </div>
                )}
              </div>
            </div>
          );
          })}
        </div>
      )}

      {/* Create Program Wizard Modal */}
      {showWizard && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            {/* Close X Button */}
            <button
              onClick={() => {
                setShowWizard(false);
                setCurrentStep('basic');
                setFormData({
                  name: '',
                  description: '',
                  logoUrl: '',
                  managers: [],
                  managerNames: [],
                  organizationId: '',
                  organizationName: '',
                  startDate: '',
                  endDate: '',
                  status: 'planned'
                });
              }}
              style={{
                position: 'absolute',
                top: '15px',
                ...(i18n.language === 'he' ? { right: '15px' } : { left: '15px' }),
                background: 'none',
                border: 'none',
                fontSize: '28px',
                color: '#666',
                cursor: 'pointer',
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                transition: 'all 0.2s ease',
                padding: 0,
                lineHeight: 1
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f0f0f0';
                e.currentTarget.style.color = '#000';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#666';
              }}
              title={t('programs.close') || 'Close'}
            >
              ×
            </button>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '24px' }}>
              {t('programs.createNew')}
            </h3>

            {/* Step Indicator */}
            <div style={{ display: 'flex', marginBottom: '30px', gap: '10px' }}>
              {(['basic', 'managers', 'details'] as WizardStep[]).map((step, index) => (
                <div
                  key={step}
                  style={{
                    flex: 1,
                    height: '4px',
                    backgroundColor: index <= (['basic', 'managers', 'details'] as WizardStep[]).indexOf(currentStep) ? '#007acc' : '#e0e0e0',
                    borderRadius: '2px'
                  }}
                />
              ))}
            </div>

            {/* Step Content */}
            {currentStep === 'basic' && (
              <div>
                <h4>{t('programs.wizard.basicInfo')}</h4>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    {t('programs.organization')} *
                  </label>
                  <select
                    value={formData.organizationId}
                    onChange={(e) => {
                      const selectedOrg = organizations.find(org => org.id === e.target.value);
                      setFormData(prev => ({ 
                        ...prev, 
                        organizationId: e.target.value,
                        organizationName: selectedOrg?.name || ''
                      }));
                    }}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '16px'
                    }}
                  >
                    <option value="">{t('programs.selectOrganization')}</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    {t('programs.name')} *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '16px'
                    }}
                    placeholder={t('programs.enterName')}
                  />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    {t('programs.description')}
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '16px',
                      minHeight: '100px',
                      resize: 'vertical'
                    }}
                    placeholder={t('programs.enterDescription')}
                  />
                </div>
              </div>
            )}

            {currentStep === 'managers' && (
              <div>
                <h4>{t('programs.wizard.managers')}</h4>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    {t('programs.searchManagers')}
                  </label>
                  <input
                    type="text"
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '16px'
                    }}
                    placeholder={t('programs.searchUsersPlaceholder')}
                  />
                  
                  {/* Search Results */}
                  {searchResults.length > 0 && (
                    <div style={{
                      border: '1px solid #ddd',
                      borderTop: 'none',
                      borderRadius: '0 0 6px 6px',
                      maxHeight: '200px',
                      overflow: 'auto',
                      backgroundColor: 'white'
                    }}>
                      {searchResults.map(user => (
                        <div
                          key={user.id}
                          onClick={() => handleAddManager(user)}
                          style={{
                            padding: '12px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f0f0f0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'white';
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: '500' }}>{user.name}</div>
                            <div style={{ fontSize: '14px', color: '#666' }}>{user.email}</div>
                          </div>
                          {formData.managers.includes(user.id) && (
                            <span style={{ color: '#28a745', fontSize: '14px' }}>✓ {t('programs.added')}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Selected Managers */}
                {formData.managerNames.length > 0 && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                      {t('programs.selectedManagers')}
                    </label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {formData.managerNames.map((name, index) => (
                        <div
                          key={formData.managers[index]}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '6px 12px',
                            backgroundColor: '#e3f2fd',
                            borderRadius: '20px',
                            fontSize: '14px'
                          }}
                        >
                          {name}
                          <button
                            onClick={() => handleRemoveManager(formData.managers[index])}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#666',
                              fontSize: '16px',
                              padding: 0
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}


            {currentStep === 'details' && (
              <div>
                <h4>{t('programs.wizard.details')}</h4>
                
                {/* Logo Upload */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    {t('programs.logo')}
                  </label>
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,.jpg,.jpeg"
                    onChange={handleLogoUpload}
                    disabled={isUploading}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px'
                    }}
                  />
                  {isUploading && (
                    <div style={{ marginTop: '8px', color: '#007acc' }}>
                      {t('programs.wizard.uploading')}...
                    </div>
                  )}
                  {formData.logoUrl && (
                    <img
                      key={formData.logoUrl}
                      src={formData.logoUrl}
                      alt="Program logo"
                      style={{
                        marginTop: '12px',
                        width: '100px',
                        height: '100px',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        border: '1px solid #ddd'
                      }}
                      onLoad={() => setIsUploading(false)}
                    />
                  )}
                </div>

              </div>
            )}

            {/* Navigation Buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '30px' }}>
              <div style={{ display: 'flex', gap: '12px' }}>
                {currentStep !== 'basic' && (
                  <button
                    onClick={() => {
                      const steps: WizardStep[] = ['basic', 'managers', 'details'];
                      const currentIndex = steps.indexOf(currentStep);
                      if (currentIndex > 0) {
                        setCurrentStep(steps[currentIndex - 1]);
                      }
                    }}
                    style={{
                      padding: '12px 24px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      fontSize: '16px'
                    }}
                  >
                    {t('programs.wizard.back')}
                  </button>
                )}
                {currentStep === 'details' ? (
                  <button
                    onClick={handleCreateProgram}
                    disabled={isCreating || !formData.name.trim() || !formData.organizationId}
                    style={{
                      padding: '12px 24px',
                      border: 'none',
                      borderRadius: '6px',
                      backgroundColor: '#007acc',
                      color: 'white',
                      cursor: isCreating || !formData.name.trim() || !formData.organizationId ? 'not-allowed' : 'pointer',
                      fontSize: '16px',
                      opacity: isCreating || !formData.name.trim() || !formData.organizationId ? 0.6 : 1
                    }}
                  >
                    {isCreating ? t('programs.creating') : t('programs.create')}
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      const steps: WizardStep[] = ['basic', 'managers', 'details'];
                      const currentIndex = steps.indexOf(currentStep);
                      if (currentIndex < steps.length - 1) {
                        setCurrentStep(steps[currentIndex + 1]);
                      }
                    }}
                    disabled={currentStep === 'basic' && (!formData.name.trim() || !formData.organizationId)}
                    style={{
                      padding: '12px 24px',
                      border: 'none',
                      borderRadius: '6px',
                      backgroundColor: '#007acc',
                      color: 'white',
                      cursor: (currentStep === 'basic' && (!formData.name.trim() || !formData.organizationId)) ? 'not-allowed' : 'pointer',
                      fontSize: '16px',
                      opacity: (currentStep === 'basic' && (!formData.name.trim() || !formData.organizationId)) ? 0.6 : 1
                    }}
                  >
                    {t('programs.wizard.next')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Program Details Modal */}
      {showDetailsModal && selectedProgram && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            {/* Close X Button */}
            <button
              onClick={() => setShowDetailsModal(false)}
              style={{
                position: 'absolute',
                top: '15px',
                ...(i18n.language === 'he' ? { right: '15px' } : { left: '15px' }),
                background: 'none',
                border: 'none',
                fontSize: '28px',
                color: '#666',
                cursor: 'pointer',
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                transition: 'all 0.2s ease',
                padding: 0,
                lineHeight: 1
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f0f0f0';
                e.currentTarget.style.color = '#000';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#666';
              }}
              title={t('programs.close') || 'Close'}
            >
              ×
            </button>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingRight: '40px' }}>
              <h3 style={{ margin: 0, fontSize: '24px' }}>
                {selectedProgram.name}
              </h3>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {detailsViewMode === 'view' && (
                  <button
                    onClick={() => {
                      setShowAddUserModal(true);
                      setShowDetailsModal(false);
                    }}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    {t('users.addUser') || 'Add User'}
                  </button>
                )}
                {detailsViewMode === 'view' && (
                  <button
                    onClick={() => setDetailsViewMode('edit')}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#007acc',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    {t('common.edit') || 'Edit'}
                  </button>
                )}
                {detailsViewMode === 'edit' && (
                  <button
                    onClick={() => setDetailsViewMode('view')}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: 'transparent',
                      color: '#666',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    {t('common.back') || 'Back'}
                  </button>
                )}
              </div>
            </div>

            {detailsViewMode === 'view' ? (
              /* Members View - Similar to Organizations */
              <div>
                {/* Get all members for this program */}
                {(() => {
                  const allMembers = allUsers.filter(u => u.programId === selectedProgram?.id);
                  const managers = editData.managers || [];
                  const regularMembers = allMembers.filter(u => !managers.includes(u.id));
                  
                  return (
                    <>
                      {/* Managers Section */}
                      <div style={{ marginBottom: '30px' }}>
                        <h4 style={{ marginBottom: '20px', color: '#333' }}>
                          {t('programs.managers') || 'Managers'}
                        </h4>
                        {managers.length === 0 ? (
                          <div style={{ 
                            textAlign: 'center', 
                            padding: '40px', 
                            border: '1px solid #e9ecef', 
                            borderRadius: '8px',
                            backgroundColor: '#f8f9fa'
                          }}>
                            <div style={{ fontSize: '32px', marginBottom: '12px' }}>👥</div>
                            <p style={{ margin: 0, color: '#666' }}>{t('members.noMembers') || 'No managers'}</p>
                          </div>
                        ) : (
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                            gap: '16px'
                          }}>
                            {managers.map((managerId) => {
                              const member = allUsers.find(u => u.id === managerId);
                              if (!member) return null;
                              
                              return (
                                <div
                                  key={managerId}
                                  style={{
                                    border: '1px solid #e9ecef',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    backgroundColor: 'white',
                                    position: 'relative'
                                  }}
                                >
                                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                    <div
                                      style={{
                                        width: '40px',
                                        height: '40px',
                                        backgroundColor: '#f8f9fa',
                                        border: '1px solid #e9ecef',
                                        borderRadius: '50%',
                                        marginRight: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '18px',
                                        fontWeight: '600',
                                        color: '#6c757d'
                                      }}
                                    >
                                      {member.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                      <h4 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600', color: '#333' }}>
                                        {member.name}
                                      </h4>
                                      <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                                        {member.email}
                                      </p>
                                    </div>
                                    {member.id !== user.id && (
                                      <button
                                        onClick={() => handleRemoveDetailsManager(member.id)}
                                        style={{
                                          position: 'absolute',
                                          top: '12px',
                                          ...(i18n.language === 'he' ? { left: '12px' } : { right: '12px' }),
                                          background: 'none',
                                          border: 'none',
                                          color: '#dc3545',
                                          cursor: 'pointer',
                                          fontSize: '20px',
                                          width: '24px',
                                          height: '24px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          borderRadius: '50%',
                                          transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor = '#fee';
                                          e.currentTarget.style.transform = 'scale(1.1)';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.backgroundColor = 'transparent';
                                          e.currentTarget.style.transform = 'scale(1)';
                                        }}
                                        title={t('programs.removeManager') || 'Remove Manager'}
                                      >
                                        ×
                                      </button>
                                    )}
                                  </div>
                                  <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                                    <div style={{ marginBottom: '4px' }}>
                                      <strong>{t('members.role') || 'Role'}:</strong> {t('programs.manager') || 'Manager'}
                                    </div>
                                    {member.lastLoginAt && (
                                      <div>
                                        <strong>{t('members.lastLogin') || 'Last Login'}:</strong> {
                                          typeof member.lastLoginAt === 'string'
                                            ? new Date(member.lastLoginAt).toLocaleDateString()
                                            : t('members.never') || 'Never'
                                        }
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Regular Members Section */}
                      <div>
                        <h4 style={{ marginBottom: '20px', color: '#333' }}>
                          {t('programs.members') || 'Members'}
                        </h4>
                        {regularMembers.length === 0 ? (
                          <div style={{ 
                            textAlign: 'center', 
                            padding: '40px', 
                            border: '1px solid #e9ecef', 
                            borderRadius: '8px',
                            backgroundColor: '#f8f9fa'
                          }}>
                            <div style={{ fontSize: '32px', marginBottom: '12px' }}>👥</div>
                            <p style={{ margin: 0, color: '#666' }}>{t('members.noMembers') || 'No members'}</p>
                          </div>
                        ) : (
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                            gap: '16px'
                          }}>
                            {regularMembers.map((member) => {
                              return (
                                <div
                                  key={member.id}
                                  style={{
                                    border: '1px solid #e9ecef',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    backgroundColor: 'white',
                                    position: 'relative'
                                  }}
                                >
                                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                    <div
                                      style={{
                                        width: '40px',
                                        height: '40px',
                                        backgroundColor: '#f8f9fa',
                                        border: '1px solid #e9ecef',
                                        borderRadius: '50%',
                                        marginRight: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '18px',
                                        fontWeight: '600',
                                        color: '#6c757d'
                                      }}
                                    >
                                      {member.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                      <h4 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600', color: '#333' }}>
                                        {member.name}
                                      </h4>
                                      <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                                        {member.email}
                                      </p>
                                    </div>
                                    {member.id !== user.id && (
                                      <button
                                        onClick={() => handleRemoveMember(member.id)}
                                        style={{
                                          position: 'absolute',
                                          top: '12px',
                                          ...(i18n.language === 'he' ? { left: '12px' } : { right: '12px' }),
                                          background: 'none',
                                          border: 'none',
                                          color: '#dc3545',
                                          cursor: 'pointer',
                                          fontSize: '20px',
                                          width: '24px',
                                          height: '24px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          borderRadius: '50%',
                                          transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor = '#fee';
                                          e.currentTarget.style.transform = 'scale(1.1)';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.backgroundColor = 'transparent';
                                          e.currentTarget.style.transform = 'scale(1)';
                                        }}
                                        title={t('programs.removeMember') || 'Remove Member'}
                                      >
                                        ×
                                      </button>
                                    )}
                                  </div>
                                  <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                                    <div style={{ marginBottom: '4px' }}>
                                      <strong>{t('members.role') || 'Role'}:</strong> {member.organizationRole || t('users.roles.social_worker') || 'Member'}
                                    </div>
                                    {member.lastLoginAt && (
                                      <div>
                                        <strong>{t('members.lastLogin') || 'Last Login'}:</strong> {
                                          typeof member.lastLoginAt === 'string'
                                            ? new Date(member.lastLoginAt).toLocaleDateString()
                                            : t('members.never') || 'Never'
                                        }
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>
            ) : (
              /* Edit View */
              <>
            {/* Program Name */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                {t('programs.name')} *
              </label>
              <input
                type="text"
                value={editData.name}
                onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* Description */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                {t('programs.description')}:
              </label>
              <textarea
                value={editData.description}
                onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
                placeholder={t('programs.enterDescription')}
                rows={3}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Logo */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                {t('programs.logo')}
              </label>
              <input
                type="file"
                accept="image/jpeg,image/jpg,.jpg,.jpeg"
                onChange={handleDetailsLogoUpload}
                disabled={isUploading}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
              {isUploading && (
                <div style={{ marginTop: '8px', color: '#666', fontSize: '14px' }}>
                  {t('programs.wizard.uploading')}...
                </div>
              )}
              {editData.logoUrl && (
                <div style={{ marginTop: '12px' }}>
                  <img
                    key={editData.logoUrl}
                    src={editData.logoUrl}
                    alt="Program logo"
                    style={{
                      maxWidth: '200px',
                      maxHeight: '100px',
                      border: '1px solid #ddd',
                      borderRadius: '6px'
                    }}
                    onLoad={() => setIsUploading(false)}
                  />
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '30px' }}>
              <button
                onClick={() => handleDeleteProgram(selectedProgram.id)}
                disabled={isDeleting}
                style={{
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '6px',
                  background: '#dc3545',
                  color: 'white',
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  opacity: isDeleting ? 0.6 : 1
                }}
              >
                {isDeleting ? t('programs.deleting') : t('programs.delete')}
              </button>
              <button
                onClick={handleUpdateProgram}
                disabled={isUpdating || !editData.name.trim()}
                style={{
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#007acc',
                  color: 'white',
                  cursor: isUpdating || !editData.name.trim() ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  opacity: isUpdating || !editData.name.trim() ? 0.6 : 1
                }}
              >
                {isUpdating ? t('programs.updating') : t('programs.update')}
              </button>
            </div>
              </>
            )}
          </div>
        </div>
      )}
      {/* Add User Modal */}
      {selectedProgram && (
        <AddUserModal
          user={user}
          isOpen={showAddUserModal}
          onClose={async () => {
            setShowAddUserModal(false);
            // Refresh the user list when modal closes
            if (selectedProgram) {
              try {
                const usersRef = collection(db, 'users');
                const snapshot = await getDocs(usersRef);
                const users: UserManagementUser[] = [];
                
                snapshot.forEach((doc) => {
                  const userData = doc.data();
                  if (!userData.deleted) {
                    users.push({
                      id: doc.id,
                      name: userData.name || '',
                      email: userData.email || '',
                      role: userData.role,
                      organizationId: userData.organizationId,
                      organizationRole: userData.organizationRole,
                      groupId: userData.groupId,
                      programId: userData.programId,
                      createdAt: userData.createdAt?.toDate?.()?.toISOString() || '',
                      lastLoginAt: userData.lastLoginAt?.toDate?.()?.toISOString() || '',
                      loginCount: userData.loginCount || 0,
                      blocked: userData.blocked || false
                    });
                  }
                });
                setAllUsers(users);
              } catch (error) {
                console.error('Error loading users:', error);
              }
            }
          }}
          organizationId={selectedProgram.organizationId}
          programId={selectedProgram.id}
          entityType="program"
          entityName={selectedProgram.name}
        />
      )}
    </div>
  );
}
